import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms'
import { ErrorMessage } from '@northfork/ng-basics'
import { Observable, of, timer } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { CorporateActionsService } from '../services/corporate-actions.service'
import { SecurityService } from '../services/security.service'

/**
 * cusipValidator
 */
export function cusipValidator(
  securityService: SecurityService,
): AsyncValidatorFn {
  // This validator is called twice in succession - when users enter input (on blur),
  // and when that input is programmatically parsed into an array of strings (setValue()),
  // so it should be debounced.
  const debouceTime = 500 // milliseconds

  return (
    control: AbstractControl,
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    return timer(debouceTime).pipe(
      switchMap(() => {
        // Assume that CUSIPs loaded from existing corporate actions are valid
        if (control.pristine) {
          return of(null)
        }

        let controlArray = control.value
        if (
          typeof control.value === 'string' ||
          control.value instanceof String
        ) {
          controlArray = control.value.split(',')
        }
        return securityService.getSecurityIdsBulk(controlArray).pipe(
          map(idsByCusip => {
            const resolvedCusips: Set<string> = new Set(idsByCusip.keys())
            const unresolvedCusips: Array<string> = controlArray.filter(
              (val: string) => !resolvedCusips.has(val),
            )
            if (unresolvedCusips.length > 0) {
              return {
                SecurityValidator: {
                  message:
                    'Unable to find security ID(s) for CUSIP(s): ' +
                    unresolvedCusips,
                },
              }
            } else {
              return null
            }
          }),
        )
      }),
    )
  }
}

/**
 *  JiraStatusAsyncValidator
 */
export function verifyJiraStatusAsyncValidator(
  corporateActionsService: CorporateActionsService,
) {
  const validatorFn: AsyncValidatorFn = (
    control: AbstractControl,
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    return corporateActionsService.verifyIssue(control.value).pipe(
      map((flag: boolean) => {
        if (!flag) {
          return {
            JiraStatusValidator: {
              message: 'Invalid Jira Key',
            },
          }
        }
        return null
      }),
    )
  }
  return validatorFn
}

export const jiraStatusValidationMessages: ErrorMessage[] = [
  {
    error: 'JiraStatusValidator',
    format: (label, error): string => {
      return error.message
    },
  },
]

export const securityValidationMessages: ErrorMessage[] = [
  {
    error: 'SecurityValidator',
    format: (label, error): string => {
      return error.message
    },
  },
]
